// Proactive medication suggestion card (PROACTIVE_SUGGESTIONS_PLAN.md).
// Extracted from ChatPane so it can render identically on a fresh chat AND on the
// Dashboard. Self-contained: it owns all suggestion-interaction state (time mode,
// per-row removal, learned→regimen add, staleness re-prompt, feedback) and writes
// via the same gate-free endpoints. The parent supplies the suggestion plus
// callbacks for what to do after a save / dismiss / regimen add.
//
// Reset-on-new-suggestion is handled by the parent passing key={suggestion.pendingId}
// so a brand-new batch remounts the card with fresh internal state.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  CUSTOM_TAKEN_AT_MAX_DAYS_BACK,
  doseControlKind,
  formatMedStrength,
  isAdjusted,
  isMedItemActive,
  usualDose,
} from "@medassist/shared";
import type { DoseControlItem, MedIngredient, MedUnit, Regimen, RegimenItem } from "@medassist/shared";
import {
  confirmEvents,
  getRegimens,
  reviseSuggestionDoses,
  saveRegimen,
  submitFeedback,
  ApiError,
  type ChatMode,
  type ProposedEvent,
  type SavedEventRef,
  type ProactiveSuggestion,
} from "../lib/api.js";
import { relativeTime } from "../lib/time.js";
import { itemForMedication, type ItemIdentity } from "../lib/itemReport.js";
import { createLatestRunner } from "../lib/latestOnly.js";
import { SuggestionDebugModal } from "./SuggestionDebugModal.js";
import { AddMedToSuggestionModal } from "./AddMedToSuggestionModal.js";
import { ItemReportModal } from "./ItemReportModal.js";
import { DoseAdjustRow, DoseCheckSpinner } from "./DoseAdjustRow.js";
import { ThumbUpIcon, ThumbDownIcon, CalendarIcon } from "./icons.js";
import { ClearableTextarea } from "./ClearableInput.js";

// ── Client-side snooze (so the card doesn't nag) ──────────────────────────────
// After Save/Dismiss we suppress re-showing for a window. Saved meds also stop
// re-proposing server-side via the already-taken check, so this mainly covers the
// "not now" dismiss case. Exported so the suggestion fetchers (ChatPane / Dashboard)
// can skip the request entirely while snoozed.
const SUGGESTION_STALE_MS = 15 * 60_000; // re-prompt after 15 min unconfirmed (§10/D-c)
const SNOOZE_DISMISS_HOURS = 4;
const SNOOZE_SAVE_HOURS = 2;

function snoozeKey(patientId: string): string {
  return `proactiveSnooze:${patientId}`;
}
export function isSuggestionSnoozed(patientId: string): boolean {
  try {
    const v = localStorage.getItem(snoozeKey(patientId));
    return !!v && Date.now() < Number(v);
  } catch {
    return false;
  }
}
export function snoozeSuggestions(patientId: string, hours: number): void {
  try {
    localStorage.setItem(snoozeKey(patientId), String(Date.now() + hours * 3_600_000));
  } catch {
    /* localStorage unavailable — snooze is best-effort */
  }
}

// medicationConfirmation() ends with "today/tonight/yesterday/date at H:MM AM".
// In the suggestion card the time is shown once globally via the summary line, so
// strip the trailing time clause from each row's description.
export function medLabelWithoutTime(confirmation: string): string {
  return confirmation.replace(
    /, (?:today|tonight|yesterday|[A-Z][a-z]{2} \d{1,2}(?:\s+\d{4})?) at \d{1,2}:\d{2} [AP]M$/,
    ""
  );
}

/** Local clock time ("8:12 AM") from an ISO string. */
function formatClock(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}
function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function formatHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function todayStr(): string {
  return localDateStr(new Date());
}
function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function payloadDose(payload: Record<string, unknown>): number | undefined {
  return typeof payload.dose === "number" && Number.isFinite(payload.dose) ? payload.dose : undefined;
}

function payloadUnit(payload: Record<string, unknown>): MedUnit | undefined {
  return typeof payload.unit === "string" ? (payload.unit as MedUnit) : undefined;
}

function payloadIngredients(payload: Record<string, unknown>): MedIngredient[] | undefined {
  return Array.isArray(payload.ingredients) && payload.ingredients.length > 0
    ? (payload.ingredients as MedIngredient[])
    : undefined;
}

/** Regimen match by name, else a step-less control synthesized from the original payload (A-4). */
function controlItemForEvent(
  event: ProposedEvent,
  usualPayload: Record<string, unknown> | undefined,
  regimens: Regimen[] | null
): (DoseControlItem & { name: string; strength: string }) | null {
  const name = typeof event.payload.name === "string" ? event.payload.name.trim() : "";
  if (!name) return null;
  const key = name.toLowerCase();
  const matched = (regimens ?? [])
    .filter((r) => r.active)
    .flatMap((r) => r.items)
    .find((it) => isMedItemActive(it) && it.name.trim().toLowerCase() === key);
  if (matched) {
    return {
      name: matched.name,
      strength: formatMedStrength(matched),
      dose: matched.dose,
      unit: matched.unit,
      pillStrength: matched.pillStrength,
      pillForm: matched.pillForm,
      pillHalfSteps: matched.pillHalfSteps,
      pillQuarterSteps: matched.pillQuarterSteps,
      ingredients: matched.ingredients,
    };
  }
  const src = usualPayload ?? event.payload;
  const dose = payloadDose(src);
  const unit = payloadUnit(src);
  const ingredients = payloadIngredients(src);
  return {
    name,
    strength: formatMedStrength({ dose, unit, ingredients }),
    dose,
    unit,
    ingredients,
  };
}

function doseEditsToList(edits: Record<number, number>): { index: number; dose: number }[] {
  return Object.entries(edits).map(([index, dose]) => ({ index: Number(index), dose }));
}

function WarningLines({
  text,
  removedBaseNames,
  dim = false,
}: {
  text: string;
  removedBaseNames: Set<string>;
  dim?: boolean;
}) {
  let inRemovedSection = false;
  return (
    <div className={dim ? "mt-1 opacity-35" : undefined}>
      {text.trim().split("\n").map((line, idx) => {
        if (!line.trim()) return <div key={idx} className="h-1.5" />;
        const isSubLine = line.startsWith("   ") || line.startsWith("\t");
        const directMatch =
          removedBaseNames.size > 0 && [...removedBaseNames].some((n) => line.toLowerCase().includes(n));
        if (!isSubLine) inRemovedSection = directMatch;
        return (
          <div
            key={idx}
            className={directMatch || (isSubLine && inRemovedSection) ? "line-through opacity-40" : undefined}
          >
            {line}
          </div>
        );
      })}
    </div>
  );
}

export function SuggestionCard({
  patientId,
  suggestion,
  mode = "capture",
  sessionId,
  isAdmin = false,
  patientName,
  timezone,
  autoCloseDays,
  complianceToleranceHours,
  onSaved,
  onDismiss,
  onAddedToRegimen,
  onSuggestionUpdated,
}: {
  patientId: string;
  suggestion: ProactiveSuggestion;
  /** Feedback snapshot mode (capture/onboarding); defaults to capture. */
  mode?: ChatMode;
  /** Associates feedback with a session when one exists. */
  sessionId?: string;
  /** Show the admin-only 🐞 debug button + modal. */
  isAdmin?: boolean;
  /** Patient context for the per-med history modal (ItemReportModal). */
  patientName: string;
  timezone: string;
  autoCloseDays: number;
  complianceToleranceHours?: number;
  /** Called after a successful save with the saved event refs (for an undo badge / refresh). */
  onSaved: (refs: SavedEventRef[]) => void;
  /** Called after "Not now" (the card has already snoozed). */
  onDismiss: () => void;
  /** Called after a learned med is saved as a regimen (so the parent can refresh config). */
  onAddedToRegimen?: () => void;
  /** Called after "+ Add another medication" produces a fresh combined suggestion
   *  (MEDICATION_BATCH_LOGGING_PLAN.md). The parent should replace its suggestion
   *  state wholesale — the new pendingId remounts this card via key={pendingId}. */
  onSuggestionUpdated?: (updated: ProactiveSuggestion) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggTimeMode, setSuggTimeMode] = useState<"now" | "scheduled" | "custom">("now");
  const [suggCustomTime, setSuggCustomTime] = useState<string>(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  });
  const [suggCustomDate, setSuggCustomDate] = useState<string>(() => todayStr());
  const [suggRemovedIdx, setSuggRemovedIdx] = useState<Set<number>>(new Set());
  const [suggNowBaseline, setSuggNowBaseline] = useState<number>(() => Date.now());
  const [suggStale, setSuggStale] = useState(false);
  const [suggAddedIdx, setSuggAddedIdx] = useState<Set<number>>(new Set());
  const [suggAddingIdx, setSuggAddingIdx] = useState<number | null>(null);
  const [suggDebugOpen, setSuggDebugOpen] = useState(false);
  const [addMedOpen, setAddMedOpen] = useState(false);
  // Per-medication history modal (same modal the Logs page opens from its calendar icon).
  const [reportItem, setReportItem] = useState<ItemIdentity | null>(null);
  // Feedback state.
  const [suggFeedbackRating, setSuggFeedbackRating] = useState<"up" | "down" | null>(null);
  const [suggFeedbackOpen, setSuggFeedbackOpen] = useState(false);
  const [suggFeedbackChecked, setSuggFeedbackChecked] = useState<Set<number>>(new Set());
  const [suggFeedbackText, setSuggFeedbackText] = useState("");
  const [suggFeedbackBusy, setSuggFeedbackBusy] = useState(false);
  const [suggFeedbackError, setSuggFeedbackError] = useState<string | null>(null);
  // Local copy so a dose revise can replace pendingId without remounting (D-10).
  const [live, setLive] = useState(suggestion);
  const [edits, setEdits] = useState<Record<number, number>>({});
  const [drawerIdx, setDrawerIdx] = useState<number | null>(null);
  const [checkState, setCheckState] = useState<"ready" | "dirty" | "checking" | "error">("ready");
  const [regimens, setRegimens] = useState<Regimen[] | null>(null);

  const patientIdRef = useRef(patientId);
  patientIdRef.current = patientId;
  const editsRef = useRef(edits);
  editsRef.current = edits;
  // Advanced inside the revise call, including when a stale response is dropped.
  // Do not copy it back from `live` on render — that would rewind to a pending the server already deleted.
  const pendingIdRef = useRef(live.pendingId);
  const reviseGate = useRef<Promise<void>>(Promise.resolve());
  const regimensLoad = useRef<Promise<void> | null>(null);
  const runnerRef = useRef<ReturnType<
    typeof createLatestRunner<[Record<number, number>], ProactiveSuggestion>
  > | null>(null);
  if (runnerRef.current === null) {
    runnerRef.current = createLatestRunner({
      delayMs: 400,
      run: (editMap) => {
        const job = reviseGate.current.then(async () => {
          const updated = await reviseSuggestionDoses(
            patientIdRef.current,
            pendingIdRef.current,
            doseEditsToList(editMap)
          );
          // A dropped result still consumed the previous pending (the server deletes it).
          // Remember the fresh id so the next edit does not 404, without flipping the card to ready.
          pendingIdRef.current = updated.pendingId;
          setLive((prev) =>
            prev.pendingId === updated.pendingId ? prev : { ...prev, pendingId: updated.pendingId }
          );
          return updated;
        });
        reviseGate.current = job.then(
          () => undefined,
          () => undefined
        );
        return job;
      },
      onStart: () => setCheckState("checking"),
      onResult: (updated) => {
        setLive((prev) => ({
          ...prev,
          pendingId: updated.pendingId,
          warningPrefix: updated.warningPrefix,
          interactionWarnings: updated.interactionWarnings,
          hiddenInteractionCount: updated.hiddenInteractionCount,
          checkedDrugNames: updated.checkedDrugNames,
          focusDrugNames: updated.focusDrugNames,
          rxnavUnreachable: updated.rxnavUnreachable,
          fallbackUsed: updated.fallbackUsed,
          overdoseWarnings: updated.overdoseWarnings,
          hiddenOverdoseCount: updated.hiddenOverdoseCount,
          sideEffectWarnings: updated.sideEffectWarnings,
          events: updated.events.map((ev, i) => ({
            ...ev,
            source: prev.events[i]?.source,
            lastDoseAt: prev.events[i]?.lastDoseAt,
          })),
        }));
        setCheckState("ready");
        setError(null);
      },
      onError: (err) => {
        setCheckState("error");
        setError(err instanceof ApiError ? err.message : "Couldn't re-check safety");
      },
    });
  }

  // Staleness re-prompt (§10/D-c): if the suggestion sits unconfirmed past 15 min,
  // flag it so the card offers to refresh the "now" times — all Bedrock-free.
  useEffect(() => {
    const t = window.setTimeout(() => setSuggStale(true), SUGGESTION_STALE_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const runner = runnerRef.current;
    return () => runner?.cancel();
  }, []);

  function editDose(index: number, dose: number) {
    const next = { ...editsRef.current, [index]: dose };
    editsRef.current = next;
    setEdits(next);
    setCheckState("dirty");
    runnerRef.current?.schedule(next);
  }

  function retrySafetyCheck() {
    setError(null);
    setCheckState("dirty");
    runnerRef.current?.schedule(editsRef.current);
  }

  function ensureRegimens() {
    if (regimensLoad.current) return;
    regimensLoad.current = getRegimens(patientId)
      .then((list) => setRegimens(list))
      .catch(() => setRegimens([]))
      .then(() => undefined);
  }

  async function saveSuggestion() {
    const keep = live.events.map((_, i) => i).filter((i) => !suggRemovedIdx.has(i));
    if (keep.length === 0) return;
    const allKept = keep.length === live.events.length;
    let customIso: string | undefined;
    if (suggTimeMode === "custom" && suggCustomTime && suggCustomDate) {
      const [year, month, day] = suggCustomDate.split("-").map(Number);
      const [h, m] = suggCustomTime.split(":").map(Number);
      const d = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, h ?? 0, m ?? 0, 0, 0);
      customIso = d.toISOString();
    }
    setBusy(true);
    setError(null);
    try {
      const { saved: refs } = await confirmEvents(
        patientId,
        live.pendingId,
        allKept ? undefined : keep,
        suggTimeMode,
        customIso
      );
      // Only snooze when the whole batch was logged. On a partial save (the user
      // removed some rows with ✕), leave snooze off so the skipped meds — still
      // within their due window and not yet logged — re-surface next time; the
      // server's already-taken check drops the ones we just saved.
      if (allKept) snoozeSuggestions(patientId, SNOOZE_SAVE_HOURS);
      onSaved(refs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  function dismissSuggestion() {
    snoozeSuggestions(patientId, SNOOZE_DISMISS_HOURS);
    onDismiss();
  }

  // Phase 2 nudge: turn a learned med (inferred from history, not in any saved
  // regimen) into a scheduled regimen in one tap — deterministic, no Bedrock. We seed
  // the regimen's defaultTime from the learned typical time (scheduledAnchorAt) so it
  // surfaces as a *scheduled* suggestion next time instead of being relearned.
  async function addLearnedToRegimen(e: ProposedEvent, idx: number) {
    const p = e.payload as Record<string, unknown>;
    const name = String(p.name ?? "").trim();
    if (!name) return;
    const anchor = e.scheduledAnchorAt ? new Date(e.scheduledAnchorAt) : null;
    const defaultTime =
      anchor && !isNaN(anchor.getTime())
        ? `${String(anchor.getHours()).padStart(2, "0")}:${String(anchor.getMinutes()).padStart(2, "0")}`
        : undefined;
    setSuggAddingIdx(idx);
    setError(null);
    try {
      await saveRegimen({
        patientId,
        name,
        kind: "scheduled",
        scheduledGroup: (p.scheduledGroup as Regimen["scheduledGroup"]) ?? "other",
        defaultTime,
        items: [
          {
            lineId: crypto.randomUUID(),
            name,
            dose: typeof p.dose === "number" ? p.dose : undefined,
            unit: p.unit as RegimenItem["unit"],
            ingredients: p.ingredients as RegimenItem["ingredients"],
            route: p.route as RegimenItem["route"],
          },
        ],
      });
      setSuggAddedIdx((prev) => new Set(prev).add(idx));
      onAddedToRegimen?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add to regimens.");
    } finally {
      setSuggAddingIdx(null);
    }
  }

  async function handleSuggFeedbackThumb(rating: "up" | "down") {
    // Use real sessionId if a session exists; otherwise use the suggestion id as a unique key.
    const fbSessionId = sessionId ?? `suggestion-${live.id}`;
    if (rating === "up") {
      setSuggFeedbackBusy(true);
      setSuggFeedbackError(null);
      try {
        await submitFeedback({
          patientId,
          sessionId: fbSessionId,
          mode,
          rating: "up",
          pendingProposal: live,
          sourceSurface: "suggestion_card",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        setSuggFeedbackRating("up");
        setSuggFeedbackOpen(false);
      } catch (err) {
        setSuggFeedbackError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
      } finally {
        setSuggFeedbackBusy(false);
      }
    } else {
      setSuggFeedbackRating("down");
      setSuggFeedbackOpen(true);
      setSuggFeedbackChecked(new Set());
      setSuggFeedbackText("");
    }
  }

  async function handleSuggFeedbackSubmitDown() {
    const fbSessionId = sessionId ?? `suggestion-${live.id}`;
    setSuggFeedbackBusy(true);
    setSuggFeedbackError(null);
    try {
      const flaggedMeds = live.events
        .filter((_, i) => suggFeedbackChecked.has(i))
        .map((e) => medLabelWithoutTime(e.confirmation))
        .join(", ");
      const parts = [
        "Medication suggestion feedback.",
        flaggedMeds ? `Flagged medications: ${flaggedMeds}.` : "",
        suggFeedbackText.trim() ? `Reason: ${suggFeedbackText.trim()}` : "",
      ].filter(Boolean);
      await submitFeedback({
        patientId,
        sessionId: fbSessionId,
        mode,
        rating: "down",
        reasonCategory: "wrong_capture",
        reasonText: parts.join("\n"),
        pendingProposal: live,
        sourceSurface: "suggestion_card",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setSuggFeedbackOpen(false);
    } catch (err) {
      setSuggFeedbackError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
    } finally {
      setSuggFeedbackBusy(false);
    }
  }

  const events = live.events;
  const hasAnchors = events.some((e) => e.scheduledAnchorAt);
  const keptCount = events.reduce((n, _e, i) => (suggRemovedIdx.has(i) ? n : n + 1), 0);

  // Base drug names (first word, lowercased) for events the user has removed.
  // Used to strike through warning lines that mention a removed medication.
  const removedBaseNames = useMemo(() => {
    const names = new Set<string>();
    for (const i of suggRemovedIdx) {
      const rawName = events[i]?.payload?.name;
      if (typeof rawName === "string" && rawName.trim()) {
        names.add(rawName.trim().toLowerCase().split(/\s/)[0]);
      }
    }
    return names;
  }, [suggRemovedIdx, events]);
  // "+ Add another medication" (D-5): only offered for medication-batch suggestions.
  const canAddMed = live.kind === "scheduled_medication" || live.kind === "learned_medication";
  const excludeNames = useMemo(() => {
    const names = new Set<string>();
    for (const e of events) {
      const rawName = e.payload?.name;
      if (typeof rawName === "string" && rawName.trim()) names.add(rawName.trim().toLowerCase());
    }
    return names;
  }, [events]);
  const keepIndexes = useMemo(
    () => events.map((_, i) => i).filter((i) => !suggRemovedIdx.has(i)),
    [events, suggRemovedIdx]
  );
  function itemForIndex(i: number) {
    const event = events[i];
    if (!event) return null;
    return controlItemForEvent(event, suggestion.events[i]?.payload, regimens);
  }

  function doseForIndex(i: number, item: DoseControlItem): number {
    const edited = edits[i];
    if (typeof edited === "number") return edited;
    return payloadDose(events[i]?.payload ?? {}) ?? usualDose(item);
  }

  let adjustedKept = 0;
  events.forEach((_e, i) => {
    if (suggRemovedIdx.has(i)) return;
    const item = itemForIndex(i);
    if (!item || doseControlKind(item) === "combo") return;
    if (isAdjusted(item, doseForIndex(i, item))) adjustedKept += 1;
  });
  const saveLabel =
    (keptCount > 0 && keptCount < events.length ? `Save ${keptCount} of ${events.length}` : "Save") +
    (adjustedKept > 0 ? ` (${adjustedKept} adjusted)` : "");

  const nowIso = new Date(suggNowBaseline).toISOString();
  const recordTime =
    suggTimeMode === "scheduled" && hasAnchors
      ? formatClock(events.find((e) => e.scheduledAnchorAt)?.scheduledAnchorAt ?? nowIso)
      : formatClock(nowIso);

  return (
    <div className="relative rounded-xl border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/40 p-4 text-sm shadow-sm">
      {/* Admin-only debug: author a "why is this (not) suggested?" prompt. */}
      {isAdmin && (
        <button
          onClick={() => setSuggDebugOpen(true)}
          title="Debug this suggestion (admin)"
          aria-label="Debug this suggestion"
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700 dark:text-indigo-500 dark:hover:bg-indigo-900/60 dark:hover:text-indigo-200 transition-colors"
        >
          🐞
        </button>
      )}
      {/* Header */}
      <p className="font-semibold text-indigo-900 dark:text-indigo-100">
        💊 {live.headline}
      </p>
      {events.length > 1 && (
        <p className="mt-0.5 text-xs text-indigo-500 dark:text-indigo-400">
          Tap ✕ to remove any you didn't take · tap an amount to change it
        </p>
      )}

      {/* Staleness re-prompt (§10): refresh the "now" baseline after 15 min. */}
      {suggStale && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-white/70 dark:bg-indigo-900/30 px-3 py-2 text-xs text-indigo-800 dark:text-indigo-200">
          <span>Some time has passed — mark these at the current time?</span>
          <button
            onClick={() => {
              setSuggNowBaseline(Date.now());
              setSuggTimeMode("now");
              setSuggStale(false);
            }}
            className="ml-auto shrink-0 rounded-full bg-(--accent) px-3 py-1 text-(--accent-contrast) hover:bg-(--accent-hover) transition-colors"
          >
            Use current time
          </button>
        </div>
      )}

      {/* Segmented pill toggle (§10): Now / Scheduled (when anchors available) / Custom. */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="flex rounded-full bg-indigo-100 dark:bg-indigo-900/70 p-0.5 text-xs font-medium">
          <button
            type="button"
            onClick={() => setSuggTimeMode("now")}
            className={
              "rounded-full px-4 py-1 transition-all " +
              (suggTimeMode === "now"
                ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                : "text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100")
            }
          >
            Now
          </button>
          {hasAnchors && (
            <button
              type="button"
              onClick={() => setSuggTimeMode("scheduled")}
              className={
                "rounded-full px-4 py-1 transition-all " +
                (suggTimeMode === "scheduled"
                  ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                  : "text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100")
              }
            >
              Scheduled
            </button>
          )}
          <button
            type="button"
            onClick={() => setSuggTimeMode("custom")}
            className={
              "rounded-full px-4 py-1 transition-all " +
              (suggTimeMode === "custom"
                ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                : "text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100")
            }
          >
            Custom
          </button>
        </div>
        {suggTimeMode === "custom" ? (
          <div className="flex gap-1.5">
            <input
              type="date"
              value={suggCustomDate}
              min={daysAgoStr(CUSTOM_TAKEN_AT_MAX_DAYS_BACK - 1)}
              max={todayStr()}
              onChange={(e) => setSuggCustomDate(e.target.value)}
              className="rounded-md border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-indigo-950 px-2 py-0.5 text-xs text-indigo-800 dark:text-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-indigo-500"
            />
            <input
              type="time"
              value={suggCustomTime}
              max={suggCustomDate === todayStr() ? formatHm(new Date()) : undefined}
              onChange={(e) => setSuggCustomTime(e.target.value)}
              className="rounded-md border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-indigo-950 px-2 py-0.5 text-xs text-indigo-800 dark:text-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-indigo-500"
            />
          </div>
        ) : (
          <span className="text-sm text-indigo-500 dark:text-indigo-400">
            records at{" "}
            <span className="font-semibold text-indigo-700 dark:text-indigo-200">{recordTime}</span>
          </span>
        )}
      </div>

      {/* Medication list */}
      <ul className="mt-3 divide-y divide-indigo-100 dark:divide-indigo-900/60">
        {events.map((e, i) => {
          const isRemoved = suggRemovedIdx.has(i);
          const isLearned = e.source === "learned";
          const isAdded = suggAddedIdx.has(i);
          return (
            <li key={i} className="flex flex-wrap items-center gap-2.5 py-1.5">
              <span className="order-1 flex min-w-0 flex-1 items-center gap-2.5">
              <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400 dark:bg-indigo-500" />
              <span
                className={
                  "flex-1 " +
                  (isRemoved
                    ? "text-indigo-700/40 line-through dark:text-indigo-200/40"
                    : "text-indigo-800 dark:text-indigo-200")
                }
              >
                {medLabelWithoutTime(e.confirmation)}
                {e.lastDoseAt && (
                  <span className={
                    "ml-2 text-xs " +
                    (isRemoved
                      ? "text-indigo-400/50 dark:text-indigo-400/40"
                      : "text-indigo-400 dark:text-indigo-500")
                  }>
                    last dose {relativeTime(e.lastDoseAt)}
                  </span>
                )}
                {/* Learned-source nudge (Phase 2): offer to save it as a regimen. */}
                {isLearned && !isRemoved && (
                  <span className="ml-2 inline-flex items-center align-middle">
                    {isAdded ? (
                      <span className="text-xs text-green-600 dark:text-green-400">✓ added to regimens</span>
                    ) : (
                      <button
                        onClick={() => void addLearnedToRegimen(e, i)}
                        disabled={busy || suggAddingIdx !== null}
                        className="text-xs text-indigo-600 underline decoration-dotted underline-offset-2 hover:text-indigo-800 disabled:opacity-50 dark:text-indigo-300 dark:hover:text-indigo-100"
                        title="Save this learned medication as a scheduled regimen"
                      >
                        {suggAddingIdx === i ? "adding…" : "+ add to a regimen"}
                      </button>
                    )}
                  </span>
                )}
              </span>
              </span>
              {!isRemoved && (() => {
                const item = itemForIndex(i);
                if (!item || doseControlKind(item) === "combo") return null;
                return (
                  <DoseAdjustRow
                    item={item}
                    dose={doseForIndex(i, item)}
                    density="compact"
                    disabled={busy}
                    open={drawerIdx === i}
                    onToggleOpen={() => {
                      ensureRegimens();
                      setDrawerIdx((cur) => (cur === i ? null : i));
                    }}
                    onChange={(dose) => editDose(i, dose)}
                  />
                );
              })()}
              {(() => {
                const p = e.payload;
                const medName = typeof p.name === "string" ? p.name : "";
                if (!medName) return null;
                return (
                  <button
                    onClick={() =>
                      setReportItem(
                        itemForMedication(medName, p as {
                          dose?: number;
                          unit?: MedUnit;
                          ingredients?: MedIngredient[];
                        })
                      )
                    }
                    disabled={busy}
                    className="order-3 flex h-6 w-6 max-sm:h-9 max-sm:w-9 shrink-0 items-center justify-center rounded-full text-indigo-300 dark:text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900/60 dark:hover:text-indigo-200 transition-colors disabled:opacity-50"
                    title="See when this was last taken and its history"
                    aria-label={`View history: ${e.confirmation}`}
                  >
                    <CalendarIcon size={14} />
                  </button>
                );
              })()}
              {events.length > 1 && (
                <button
                  onClick={() =>
                    setSuggRemovedIdx((prev) => {
                      const next = new Set(prev);
                      if (isRemoved) next.delete(i);
                      else {
                        next.add(i);
                        setDrawerIdx((cur) => (cur === i ? null : cur));
                      }
                      return next;
                    })
                  }
                  disabled={busy}
                  className={
                    "order-4 flex h-6 w-6 max-sm:h-9 max-sm:w-9 shrink-0 items-center justify-center rounded-full text-xs max-sm:text-lg leading-none transition-colors disabled:opacity-50 " +
                    (isRemoved
                      ? "bg-indigo-100 dark:bg-indigo-800 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-700"
                      : "text-indigo-300 dark:text-indigo-600 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400")
                  }
                  title={isRemoved ? "Add back" : "Don't save this one"}
                  aria-label={isRemoved ? `Add back: ${e.confirmation}` : `Remove: ${e.confirmation}`}
                >
                  {isRemoved ? "↶" : "✕"}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Safety warnings (allergy / interaction / overdose). Re-check after a dose edit (D-3). */}
      {(checkState !== "ready" || live.warningPrefix?.trim()) && (
        <div
          className={
            "mt-3 rounded-lg border px-3 py-2 text-xs " +
            (checkState === "error"
              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
              : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200")
          }
        >
          {checkState === "error" ? (
            <>
              <p className="font-semibold">Couldn&apos;t re-check safety</p>
              <p className="mt-1">
                Your changes are kept.{" "}
                <button
                  type="button"
                  onClick={retrySafetyCheck}
                  className="underline decoration-dotted underline-offset-2"
                >
                  Retry
                </button>
              </p>
            </>
          ) : checkState !== "ready" ? (
            <>
              <p className="flex items-center gap-2 font-semibold">
                <DoseCheckSpinner className="h-3.5 w-3.5" />
                Re-checking safety for your changes…
              </p>
              {live.warningPrefix?.trim() && (
                <WarningLines text={live.warningPrefix} removedBaseNames={removedBaseNames} dim />
              )}
            </>
          ) : (
            <WarningLines text={live.warningPrefix ?? ""} removedBaseNames={removedBaseNames} />
          )}
        </div>
      )}

      {canAddMed && (
        <button
          onClick={() => setAddMedOpen(true)}
          disabled={busy || checkState !== "ready"}
          className="mt-2 text-xs text-indigo-600 underline decoration-dotted underline-offset-2 hover:text-indigo-800 disabled:opacity-50 dark:text-indigo-300 dark:hover:text-indigo-100"
        >
          + Add another medication
        </button>
      )}

      {error && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => void saveSuggestion()}
          disabled={busy || keptCount === 0 || checkState !== "ready"}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50 transition-colors"
        >
          {busy ? (
            "Saving…"
          ) : checkState === "error" ? (
            "Retry check to save"
          ) : checkState !== "ready" ? (
            <>
              <DoseCheckSpinner className="h-3.5 w-3.5" onAccent />
              Checking safety…
            </>
          ) : (
            saveLabel
          )}
        </button>
        <button
          onClick={dismissSuggestion}
          disabled={busy}
          className="px-2 py-2 text-sm text-indigo-400 dark:text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors disabled:opacity-50"
        >
          Not now
        </button>
      </div>

      {/* Suggestion feedback */}
      {!suggFeedbackOpen && (
        <div className="mt-3 flex items-center gap-2 border-t border-indigo-100 dark:border-indigo-900/50 pt-2">
          <span className="text-xs text-indigo-400 dark:text-indigo-500">Helpful suggestion?</span>
          <button
            onClick={() => void handleSuggFeedbackThumb("up")}
            disabled={suggFeedbackBusy}
            title="Thumbs up"
            aria-label="Suggestion helpful"
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (suggFeedbackRating === "up"
                ? "text-green-600 dark:text-green-400"
                : "text-indigo-300 hover:text-green-600 dark:text-indigo-600 dark:hover:text-green-400")
            }
          >
            <ThumbUpIcon size={15} />
          </button>
          <button
            onClick={() => void handleSuggFeedbackThumb("down")}
            disabled={suggFeedbackBusy}
            title="Thumbs down — report incorrect suggestion"
            aria-label="Suggestion not helpful"
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (suggFeedbackRating === "down"
                ? "text-red-600 dark:text-red-400"
                : "text-indigo-300 hover:text-red-600 dark:text-indigo-600 dark:hover:text-red-400")
            }
          >
            <ThumbDownIcon size={15} />
          </button>
          {suggFeedbackRating === "up" && (
            <span className="text-xs text-indigo-400 dark:text-indigo-500">Thanks!</span>
          )}
          {suggFeedbackRating === "down" && !suggFeedbackOpen && (
            <button
              onClick={() => setSuggFeedbackOpen(true)}
              className="text-xs text-indigo-400 hover:underline dark:text-indigo-500"
            >
              Edit reason
            </button>
          )}
          {suggFeedbackError && (
            <span className="text-xs text-red-600 dark:text-red-400">{suggFeedbackError}</span>
          )}
        </div>
      )}
      {suggFeedbackOpen && (
        <div className="mt-3 space-y-2 border-t border-indigo-100 dark:border-indigo-900/50 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
              Which medications shouldn't be suggested here?
            </span>
            <button
              onClick={() => setSuggFeedbackOpen(false)}
              className="ml-auto text-xs text-indigo-400 hover:text-indigo-600 dark:text-indigo-500"
              aria-label="Close feedback form"
            >
              ✕
            </button>
          </div>
          <div className="space-y-1">
            {events.map((e, i) => (
              <label key={i} className="flex cursor-pointer items-center gap-2 text-xs text-indigo-800 dark:text-indigo-200">
                <input
                  type="checkbox"
                  checked={suggFeedbackChecked.has(i)}
                  onChange={() =>
                    setSuggFeedbackChecked((prev) => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      return next;
                    })
                  }
                  className="accent-red-500"
                />
                {medLabelWithoutTime(e.confirmation)}
              </label>
            ))}
          </div>
          <ClearableTextarea
            value={suggFeedbackText}
            onChange={(e) => setSuggFeedbackText(e.target.value)}
            placeholder="Why shouldn't these be suggested? (optional)"
            rows={2}
            className="w-full resize-none rounded border border-indigo-200 dark:border-indigo-700 bg-white dark:bg-indigo-950 px-2 py-1.5 text-xs text-indigo-900 dark:text-indigo-100 outline-none focus:ring-1 focus:ring-red-400"
          />
          {suggFeedbackError && (
            <p className="text-xs text-red-600 dark:text-red-400">{suggFeedbackError}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => void handleSuggFeedbackSubmitDown()}
              disabled={suggFeedbackBusy || (suggFeedbackChecked.size === 0 && !suggFeedbackText.trim())}
              className="rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
            >
              {suggFeedbackBusy ? "Sending…" : "Submit"}
            </button>
            <button
              onClick={() => setSuggFeedbackOpen(false)}
              className="rounded border border-indigo-200 dark:border-indigo-700 px-3 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Admin-only proactive-suggestion debug prompt modal. */}
      {suggDebugOpen && (
        <SuggestionDebugModal
          patientId={patientId}
          suggestion={live}
          onClose={() => setSuggDebugOpen(false)}
        />
      )}

      {/* "+ Add another medication" (MEDICATION_BATCH_LOGGING_PLAN.md Phase 3). */}
      {addMedOpen && (
        <AddMedToSuggestionModal
          patientId={patientId}
          excludeNames={excludeNames}
          pendingId={live.pendingId}
          keepIndexes={keepIndexes}
          onClose={() => setAddMedOpen(false)}
          onAdded={(updated) => onSuggestionUpdated?.(updated)}
          onError={(message) => setError(message)}
        />
      )}

      {/* Per-medication history — the same modal the Logs page opens from its
          calendar icon, so the user can verify the last dose / stats before
          marking a suggested med as taken. */}
      {reportItem && (
        <ItemReportModal
          patientId={patientId}
          patientName={patientName}
          timezone={timezone}
          item={reportItem}
          autoCloseDays={autoCloseDays}
          complianceToleranceHours={complianceToleranceHours}
          onClose={() => setReportItem(null)}
        />
      )}
    </div>
  );
}
